import { useParams, Link } from "react-router-dom";
import { allArticles, categories } from "@/mocks/siteData";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = allArticles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = allArticles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleHeroUrl =
    "https://readdy.ai/api/search-image?query=Japanese%20manga%20doujinshi%20art%20illustration%20with%20warm%20lighting%20and%20clean%20composition%2C%20soft%20pastel%20background%2C%20manga%20style%20aesthetic%2C%20editorial%20photography%2C%20high%20quality%2C%20minimalist%20composition&width=1600&height=600&seq=article-hero-generic&orientation=landscape";

  return (
    <>
      <SEOHead
        title={`${article.title} - 本子漫画导航`}
        description={article.excerpt}
        keywords={`本子漫画,${article.tags.join(",")},日系漫画本子,漫画同人志`}
        canonicalPath={`/article/${articleId}`}
        ogType="article"
        ogImage={articleHeroUrl}
      />
      <div className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb />

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
          <header className="mb-8">
            {category && (
              <Link
                to={`/category/${category.id}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 mb-4 transition-colors"
              >
                <i className="ri-folder-line"></i>
                {category.name}
              </Link>
            )}
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.readCount.toLocaleString()} 阅读
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-8">
            <img src={articleHeroUrl} alt={article.title} className="w-full h-full object-cover" />
          </div>

          <div
            className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:text-foreground-700 [&_p]:leading-relaxed
              [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:mb-2 [&_li]:text-foreground-700
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200 mt-12 pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-500">
                  本文由 <strong className="text-foreground-700">{article.author}</strong> 发布于 {article.date}
                </p>
              </div>
              <Link
                to={category ? `/category/${category.id}` : "/"}
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回{category ? category.name : "首页"}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    id={ra.id}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    categoryName={category?.name || ""}
                    tags={ra.tags}
                    author={ra.author}
                    date={ra.date}
                    readCount={ra.readCount}
                    imageUrl={ra.imageUrl}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              author: {
                "@type": "Person",
                name: article.author,
              },
              datePublished: article.date,
              image: articleHeroUrl,
              publisher: {
                "@type": "Organization",
                name: "本子漫画导航",
              },
            }),
          }}
        />
      </div>
    </>
  );
}